import * as React from "react";
import {RouteComponentProps} from "react-router";
import {Link} from "react-router-dom";
import {ConstColor} from "src/config/styles_const/styles_const";
import {createEntryScoutHistoryAPI} from "src/infra/api/service/entry_scout_history_api";
import Assets from "src/presentation/assets/img/assets";
import GoldLink from "src/presentation/common/atom/btn/gold_link";
import {toDateString} from "src/util/commonFuntion";
import styled from "styled-components";
import {routeConfig} from "../../config/language/ja-JP/presentation/route_text";
import {AppNotificationMessage, AppNotificationType,} from "../../domain/model/app_notification_message";
import {FrontDate} from "../../domain/model/frontDate";
import {IEntryScoutHistoryRepository} from "../../domain/repository/entry_scout_history_repository";
import {createApiClient, IApiClient} from "../../infra/api/client";
import {entity} from "../../infra/api/rpc/api";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import AppNotificationWithTexts from "../common/organism/app_notification_with_texts";
import Progress from "../common/organism/progress";
import {TBreadcrumbItem} from "../type/common";
import Faq from "../component/faq";

const apiClient: IApiClient = createApiClient();
const entryScoutHistoryRepository: IEntryScoutHistoryRepository =
    createEntryScoutHistoryAPI(apiClient);

interface IProps extends RouteComponentProps<{}> {
}

const ClientEntryScoutHistoryList: React.FC<IProps> = (props) => {
    const history = props.history;
    const [histories, setHistories] = React.useState<
        entity.IEntryScoutHistory[] | null
    >(null);
    const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);
    const [archive, setArchive] = React.useState<boolean>(
        history.location.hash === "#archive"
    );

    React.useEffect(() => {
        try {
            entryScoutHistoryRepository
                .getAllByClient(1, 1000, archive)
                .then((items) => {
                    setHistories(items);
                });
        } catch (e) {
            setMessages([
                AppNotificationMessage.from(
                    "",
                    AppNotificationType.ERROR,
                    "エラーが発生しました",
                    FrontDate.fromEmpty()
                ),
            ]);
        }
    }, []);

    const toggleArchive = async (target: entity.IEntryScoutHistory) => {
        await entryScoutHistoryRepository.toggleArchive(
            target.projectID!,
            target.customerID!,
            !target.isArchive
        );

        setHistories(histories!.filter((item) => item.id !== target.id));
    };

    const changeArchivePage = async () => {
        const toArchive = !archive;

        const items = await entryScoutHistoryRepository.getAllByClient(
            1,
            1000,
            toArchive
        );

        if (toArchive) {
            history.push("/client/entry_scout_histories#archive");
        } else {
            history.push("/client/entry_scout_histories");
        }

        setHistories(items);
        setArchive(toArchive);
    };

    // 初期化
    if (histories == null) {
        return <Progress canAnimation={false}/>;
    }

    const breadcrumbItems: TBreadcrumbItem[] = [
        routeConfig.clientRoot,
        {text: `${archive ? "アーカイブ済み一覧" : "エントリー一覧"}`},
    ];

    return (
        <React.Fragment>
            {messages.length > 0 && (
                <AppNotificationWithTexts
                    onClose={(id: string) =>
                        (event: any): void => {
                            setMessages([]);
                        }}
                    appNotificationMessages={messages}
                />
            )}

            <PageWrapper>
                <Breadcrumbs breadcrumbItems={breadcrumbItems}/>

                <Faq title={"成約報告について"}
                     text={"掲載した案件が成約した場合、企業はEXPERT NETWORK上で成約を報告する必要がります。成約は案件管理画面から行い、そこから成約情報を送信して頂きます。"}
                     link={
                         "https://branch-saturn-9ef.notion.site/180b91fe393e80e1a9e8db0cacd6e807"
                     }/>

                <div style={{display: "flex", flexDirection: "row-reverse"}}>
                    <ArchiveLink
                        to={`#`}
                        onClick={() => {
                            changeArchivePage();
                        }}
                    >
                        {archive ? "エントリー一覧に戻る" : "アーカイブ済みはこちら"}
                    </ArchiveLink>
                </div>

                <List>
                    {histories.map((item, i) => {
                        return (
                            <Item key={i}>
                                <ItemSection>
                                    {item.isScout && <ScoutLabel>スカウト</ScoutLabel>}
                                    {!item.isScout && <EntryLabel>エントリー</EntryLabel>}
                                    <DateLabel>{toDateString(item.createdAt!)}</DateLabel>
                                    <ProjectNameLabel
                                        target="_blank"
                                        to={`/client/project/preview/${item.projectID}`}
                                    >
                                        {item.projectName}
                                    </ProjectNameLabel>
                                </ItemSection>
                                <ItemSection>
                                    <AvatarLink
                                        target="_blank"
                                        to={`/client/scout/customer/${item.customerID}`}
                                    >
                                        <Avatar src={item.customerSignedIconURL || ""}/>
                                    </AvatarLink>

                                    <CustomerInfo>
                                        <Catchphrase
                                            target="_blank"
                                            to={`/client/scout/customer/${item.customerID}`}
                                        >
                                            {item.customerCatchphrase}
                                        </Catchphrase>
                                        <div style={{display: "flex", alignItems: "center"}}>
                                            <JobCareerCatchphrase
                                                target="_blank"
                                                to={`/client/scout/customer/${item.customerID}`}
                                            >
                                                {item.customerJobCareerCatchphrase}
                                            </JobCareerCatchphrase>
                                            {item.customerJobCareerCatchphrase!.length > 0 && (
                                                <CustomerInfoLinkIcon
                                                    src={Assets.up_right_from_square_solid}
                                                />
                                            )}
                                        </div>
                                    </CustomerInfo>

                                    <LinkInfo>
                                        <BtnLinkWrapper>
                                            <ToggleArchiveLink
                                                to={"#"}
                                                onClick={() => {
                                                    toggleArchive(item);
                                                }}
                                            >
                                                {item.isArchive ? "アーカイブを戻す" : "アーカイブ"}
                                            </ToggleArchiveLink>
                                            {!item.hasContract && (
                                                <ContractLink
                                                    to={`/client/contract/registration/${item.companyID}/${item.projectID}/${item.customerID}`}
                                                >
                                                    成約報告
                                                </ContractLink>
                                            )}
                                            {item.hasContract && (
                                                <ContractDisableLink>成約報告済み</ContractDisableLink>
                                            )}
                                            <MessageLink
                                                to={`/client/message/${item.projectID}/${item.customerID}/${item.companyID}`}
                                            >
                                                メッセージ
                                            </MessageLink>
                                            <Budge>{item.messageCount}</Budge>
                                        </BtnLinkWrapper>
                                    </LinkInfo>
                                </ItemSection>
                            </Item>
                        );
                    })}
                </List>
            </PageWrapper>
        </React.Fragment>
    );
};

export default ClientEntryScoutHistoryList;

const List = styled.div`
    width: 100%;
`;

const Item = styled.div`
    padding: 5px 5px;
    width: 100%;
    border-bottom: 1px solid ${ConstColor.PlaceholderAndBorder};
`;

const ItemSection = styled.div`
    display: flex;
    align-items: center;
    padding: 5px 0px;
`;

const EntryLabel = styled.div`
    margin-right: 10px;
    background: ${ConstColor.Success};
    border-radius: 4px;
    padding: 4px 15px;
    font-size: 9px;
    font-weight: bold;
    color: white;
`;

const ScoutLabel = styled.div`
    margin-right: 10px;
    background: #9a8c64;
    border-radius: 4px;
    padding: 4px 15px;
    font-size: 9px;
    font-weight: bold;
    color: white;
`;

const DateLabel = styled.div`
    font-size: 11px;
    color: #a4a4a4;
    margin-right: 7px;
`;

const ProjectNameLabel = styled(Link)`
    font-size: 11px;
    font-weight: bold;
    color: #333333;
    margin-right: 7px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;

    ::after {
        font-family: "Font Awesome 5 Free";
        content: " 35d ";
        margin-left: 5px;
    }
`;

const AvatarLink = styled(Link)`
    display: block;
`;

const Avatar = styled.img`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
`;

const CustomerInfo = styled.div`
    flex-grow: 1;
    padding: 0px 10px;
`;

const Catchphrase = styled(Link)`
    font-size: 13px;
    font-weight: bold;
    color: #333333;
    margin-right: 7px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 24px;
`;

const JobCareerCatchphrase = styled(Link)`
    font-size: 15px;
    font-weight: bold;
    color: #333333;
    margin-right: 7px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 24px;
`;

const CustomerInfoLinkIcon = styled.img`
    width: 10px;
    height: 10px;
`;

const LinkInfo = styled.div``;

const BtnLinkWrapper = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    width: 370px;
    height: 50px;
    flex-wrap: wrap;
`;

const ToggleArchiveLink = styled(GoldLink)`
    margin-right: 10px;
    margin-top: 10px;
    margin-right: 10px;
    font-size: 12px;
    text-align: center;
`;

const ContractLink = styled(GoldLink)`
    margin-right: 10px;
    margin-top: 10px;
    margin-right: 10px;
    font-size: 12px;
    text-align: center;
`;

const ContractDisableLink = styled.div`
    color: #a4a4a4;
    margin-right: 10px;
    margin-top: 10px;
    margin-right: 10px;
    font-size: 12px;
    font-weight: bold;
    text-align: center;
    padding: 4px 20px;
`;

const MessageLink = styled(GoldLink)`
    margin-top: 10px;
    margin-right: 5px;
    font-size: 12px;
    text-align: center;
`;

const Budge = styled.div`
    width: 20px;
    height: 20px;
    font-size: 9px;
    font-weight: bold;
    color: #fff;
    border-radius: 10px;
    background-color: #9a8c64;
    text-align: center;
    line-height: 20px;
`;

const ArchiveLink = styled(GoldLink)`
    width: 155px;
    margin-top: 10px;
    display: block;
    padding: 5px 0px 5px 0px;
    font-size: 12px;
    text-align: center;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/client_entry_scout_history_list_container.tsx